import {
  AfterViewInit,
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { MatSidenav } from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { SideMenusService } from '../../side-menus/side-menus.service';
import { ClarityMenuComponent } from '../../side-menus/clarity-menu/clarity-menu.component';

@Component({
  selector: 'app-clarity-layout',
  templateUrl: './clarity-layout.component.html',
  styleUrls: ['./clarity-layout.component.scss']
})
export class ClarityLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.layout-clarity') layoutFlag = true;
  @HostBinding('class.server-side-rendered') serverSideRendered: boolean;
  @HostBinding('class.main-sidenav-opened') mainMenuOpened = false; // keep compatibility with existing CSS patterns
  @HostBinding('class.alt-sidenav-opened') altSidenavOpened = false;

  /** We keep ONLY the alt sidenav (dynamic menu) as in your prior layout */
  @ViewChild('altSidenav') altSidenav!: MatSidenav;

  /** Main menu is now ClarityMenu instead of MatSidenav */
  @ViewChild('clarityMenu') clarityMenu!: ClarityMenuComponent;

  // Subscriptions
  private mainMenuToggleSub?: Subscription;
  private altMenuToggleSub?: Subscription;
  private routeChangeSub?: Subscription;
  showClarityMenu = false;

  constructor(
    private sideMenusService: SideMenusService,
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router
  ) {
    this.serverSideRendered = isPlatformServer(this.platformId);
    this.mainMenuOpened = this.sideMenusService.getMainMenuState();
  }

  ngOnInit(): void {
    this.updateMenuVisibility(this.router.url);
    this.routeChangeSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateMenuVisibility((event as NavigationEnd).urlAfterRedirects);
      });
  }

  ngAfterViewInit(): void {
    // Replace toggleMainMenu behavior: route it to ClarityMenu
    this.mainMenuToggleSub = this.sideMenusService.toggleMainMenuSubject.subscribe(event => {
      switch (event) {
        case 'toggle':
          this.clarityMenu?.toggleMenu();
          break;
        case 'open':
          this.clarityMenu?.openMenu();
          break;
        case 'close':
          this.clarityMenu?.closeMenu();
          break;
        default:
          this.clarityMenu?.toggleMenu();
      }
    });

    // Keep alt menu behavior identical (MatSidenav still used for alt)
    this.altMenuToggleSub = this.sideMenusService.toggleAltMenuSubject.subscribe(event => {
      switch (event) {
        case 'toggle':
          this.altSidenav.toggle();
          break;
        case 'open':
          this.altSidenav.open();
          break;
        case 'close':
          this.altSidenav.close();
          break;
        default:
          this.altSidenav.toggle();
      }
    });

    const shouldOpen = this.sideMenusService.getMainMenuState();
    if (shouldOpen) {
      this.clarityMenu?.openMenu();
    } else {
      this.clarityMenu?.closeMenu();
    }
  }

  ngOnDestroy(): void {
    this.mainMenuToggleSub?.unsubscribe();
    this.altMenuToggleSub?.unsubscribe();
    this.routeChangeSub?.unsubscribe();
  }

  /** Sync shared state when ClarityMenu opens/closes (keeps other components in sync) */
  onMainMenuOpenChange(opened: boolean): void {
    this.mainMenuOpened = opened;
    this.sideMenusService.setMainMenuState(opened);
  }

  altSidenavToggled(opened: boolean): void {
    this.altSidenavOpened = opened;
  }

  private updateMenuVisibility(url: string): void {
    const coachMenuAllowedRoutes = ['/ai/clarity', '/ai/clarichat', '/ai/compare-mode', '/search-history'];
    this.showClarityMenu = coachMenuAllowedRoutes.some((route) => url.includes(route));
  }
}
